import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { BARRIER_SPECS } from '../constants.js';

/**
 * Build a merged mesh for all OSM barrier ways (walls, fences, hedges, …).
 * Each barrier is rendered as a continuous vertical quad strip following the polyline.
 *
 * @param {Array}    barriers  Parsed barrier objects: { id, barrierType, nodes }
 * @param {Object}   proj      Projection helper
 * @param {Function} elevFn    Optional: elevFn(x, z) → metres above scene centre
 * @returns {THREE.Group}
 */
export function buildBarrierMesh(barriers, proj, elevFn = null) {
  // Bucket geometries by type so each type gets its own colour
  const buckets = {};

  for (const barrier of barriers) {
    const spec = BARRIER_SPECS[barrier.barrierType];
    if (!spec) continue;

    const pts = _project(barrier.nodes, proj);
    if (pts.length < 2) continue;

    const geos = _buildVerticalStrip(pts, spec.height, elevFn);
    if (geos.length === 0) continue;

    if (!buckets[barrier.barrierType]) buckets[barrier.barrierType] = [];
    buckets[barrier.barrierType].push(...geos);
  }

  const group = new THREE.Group();

  for (const [type, geos] of Object.entries(buckets)) {
    if (geos.length === 0) continue;
    const merged = mergeGeometries(geos, false);
    geos.forEach(g => g.dispose());

    const spec = BARRIER_SPECS[type];
    const mat  = new THREE.MeshLambertMaterial({
      color: spec.color,
      side:  THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = mesh.receiveShadow = true;
    group.add(mesh);
  }

  return group;
}

// ── Geometry helpers ──────────────────────────────────────────────────────────

/**
 * For each consecutive pair of points, emit a vertical quad face.
 * Returns an array of BufferGeometry objects (one per segment).
 */
function _buildVerticalStrip(pts, height, elevFn) {
  const geos = [];

  for (let i = 0; i < pts.length - 1; i++) {
    const a  = pts[i], b = pts[i + 1];
    const mx = (a.x + b.x) * 0.5, mz = (a.z + b.z) * 0.5;
    const y0 = elevFn ? elevFn(mx, mz) : 0;

    // 4 vertices: bottom-a, bottom-b, top-b, top-a
    const pos = new Float32Array([
      a.x, y0,          a.z,
      b.x, y0,          b.z,
      b.x, y0 + height, b.z,
      a.x, y0 + height, a.z,
    ]);
    const idx = [0, 1, 2,  0, 2, 3,  2, 1, 0,  3, 2, 0]; // both sides

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    geos.push(geo);
  }

  return geos;
}

function _project(nodes, proj) {
  const result = [];
  for (const n of nodes) {
    if (n.lat === undefined) continue;
    const p = proj.project(n.lat, n.lon);
    if (!isFinite(p.x) || !isFinite(p.z)) continue;
    if (result.length > 0) {
      const prev = result[result.length - 1];
      const dx = p.x - prev.x, dz = p.z - prev.z;
      if (dx * dx + dz * dz < 0.01) continue; // skip duplicates
    }
    result.push(p);
  }
  return result;
}
